(() => {
  'use strict';

  const FOOTER_SELECTOR = '[data-mbb-footer]';
  const TOGGLE_SELECTOR = '[data-mbb-footer-toggle]';
  const CONTENT_SELECTOR = '[data-mbb-footer-content]';
  const REVEAL_SELECTOR = '[data-mbb-footer-reveal]';
  const instances = new WeakMap();

  class MbbFooter {
    constructor(footer) {
      this.footer = footer;
      this.toggles = [...footer.querySelectorAll(TOGGLE_SELECTOR)];
      this.revealItems = [...footer.querySelectorAll(REVEAL_SELECTOR)];
      this.mobileQuery = window.matchMedia('(max-width:749px)');
      this.observer = null;

      this.handleBreakpointChange =
        this.handleBreakpointChange.bind(this);

      this.init();
    }

    init() {
      this.initAccordion();
      this.initReveal();
      this.handleBreakpointChange();

      this.mobileQuery.addEventListener(
        'change',
        this.handleBreakpointChange
      );
    }

    initAccordion() {
      this.toggles.forEach((toggle) => {
        toggle.addEventListener('click', () => {
          if (!this.mobileQuery.matches) return;

          const column = toggle.closest('.mbb-footer__column--menu');
          const content = column?.querySelector(CONTENT_SELECTOR);

          if (!column || !content) return;

          const willOpen = !column.classList.contains('is-open');

          this.closeOtherMenus(column);
          this.setMenuState(column, toggle, content, willOpen);
        });
      });
    }

    setMenuState(column, toggle, content, isOpen) {
      column.classList.toggle('is-open', isOpen);
      toggle.setAttribute('aria-expanded', String(isOpen));

      if (isOpen) {
        content.style.height = `${content.scrollHeight}px`;
      } else {
        content.style.height = `${content.scrollHeight}px`;

        window.requestAnimationFrame(() => {
          content.style.height = '0px';
        });
      }
    }

    closeOtherMenus(activeColumn) {
      this.footer
        .querySelectorAll('.mbb-footer__column--menu.is-open')
        .forEach((column) => {
          if (column === activeColumn) return;

          const toggle = column.querySelector(TOGGLE_SELECTOR);
          const content = column.querySelector(CONTENT_SELECTOR);

          if (!toggle || !content) return;

          this.setMenuState(column, toggle, content, false);
        });
    }

    handleBreakpointChange() {
      this.toggles.forEach((toggle) => {
        const column = toggle.closest('.mbb-footer__column--menu');
        const content = column?.querySelector(CONTENT_SELECTOR);

        if (!column || !content) return;

        column.classList.remove('is-open');

        if (this.mobileQuery.matches) {
          toggle.setAttribute('aria-expanded', 'false');
          content.style.height = '0px';
        } else {
          toggle.setAttribute('aria-expanded', 'true');
          content.style.removeProperty('height');
        }
      });
    }

    initReveal() {
      if (
        window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
        !('IntersectionObserver' in window)
      ) {
        this.showAll();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            entry.target.classList.add('is-visible');
            this.observer.unobserve(entry.target);
          });
        },
        {
          threshold: 0.12,
          rootMargin: '0px 0px -30px'
        }
      );

      this.revealItems.forEach((item, index) => {
        item.style.setProperty('--mbb-delay', `${index * 90}ms`);
        this.observer.observe(item);
      });
    }

    showAll() {
      this.revealItems.forEach((item) => {
        item.classList.add('is-visible');
      });
    }

    destroy() {
      this.mobileQuery.removeEventListener(
        'change',
        this.handleBreakpointChange
      );

      this.observer?.disconnect();
      instances.delete(this.footer);
    }
  }

  function initFooter(footer) {
    if (!footer || instances.has(footer)) return;

    instances.set(footer, new MbbFooter(footer));
  }

  function initAll(scope = document) {
    if (scope.matches?.(FOOTER_SELECTOR)) {
      initFooter(scope);
    }

    scope
      .querySelectorAll?.(FOOTER_SELECTOR)
      .forEach(initFooter);
  }

  function destroyFooter(footer) {
    instances.get(footer)?.destroy();
  }

  function boot() {
    initAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, {
      once: true
    });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const footer =
      event.target.matches?.(FOOTER_SELECTOR)
        ? event.target
        : event.target.querySelector?.(FOOTER_SELECTOR);

    if (footer) {
      destroyFooter(footer);
    }
  });

  document.addEventListener('shopify:block:select', (event) => {
    const column = event.target.closest('.mbb-footer__column');
    const toggle = column?.querySelector(TOGGLE_SELECTOR);
    const content = column?.querySelector(CONTENT_SELECTOR);

    event.target
      .closest(REVEAL_SELECTOR)
      ?.classList.add('is-visible');

    if (
      !column ||
      !toggle ||
      !content ||
      !window.matchMedia('(max-width:749px)').matches
    ) {
      return;
    }

    column.classList.add('is-open');
    toggle.setAttribute('aria-expanded', 'true');
    content.style.height = `${content.scrollHeight}px`;
  });
})();